import React, { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

/* ─── Hex grid helpers ─── */
const GRID_COLS = 7;
const GRID_ROWS = 5;

function hexId(col, row) { return `${col},${row}`; }

function neighbors(col, row) {
  const offset = row % 2 === 0 ? 0 : 1;
  return [
    [col - 1, row], [col + 1, row],
    [col - 1 + offset, row - 1], [col + offset, row - 1],
    [col - 1 + offset, row + 1], [col + offset, row + 1],
  ].filter(([c, r]) => c >= 0 && c < GRID_COLS && r >= 0 && r < GRID_ROWS);
}

function bfs(start, end, blocked) {
  const visited = new Set([start]);
  const queue = [[start, 0]];
  while (queue.length) {
    const [curr, dist] = queue.shift();
    if (curr === end) return dist;
    const [c, r] = curr.split(',').map(Number);
    for (const [nc, nr] of neighbors(c, r)) {
      const id = hexId(nc, nr);
      if (!visited.has(id) && !blocked.has(id)) {
        visited.add(id);
        queue.push([id, dist + 1]);
      }
    }
  }
  return Infinity;
}

function generateLevel(seed) {
  const rng = (max) => {
    seed = (seed * 1664525 + 1013904223) & 0xffffffff;
    return Math.abs(seed) % max;
  };
  const blocked = new Set();
  const count = 6 + rng(5);
  while (blocked.size < count) {
    const c = 1 + rng(GRID_COLS - 2);
    const r = 1 + rng(GRID_ROWS - 2);
    const id = hexId(c, r);
    if (id !== '0,0' && id !== `${GRID_COLS - 1},${GRID_ROWS - 1}`) blocked.add(id);
  }
  const optimal = bfs('0,0', `${GRID_COLS - 1},${GRID_ROWS - 1}`, blocked);
  if (optimal === Infinity || optimal > 20) return generateLevel(seed + 1);
  return { blocked, optimal };
}

/* ─── Hex position in px ─── */
function hexPos(col, row, size = 30) {
  const w = size * 2;
  const h = Math.sqrt(3) * size;
  const x = col * w * 0.75 + size;
  const y = row * h + (col % 2 === 0 ? 0 : h / 2) + size;
  return { x, y };
}

function hexPoints(cx, cy, size = 28) {
  return Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 3) * i - Math.PI / 6;
    return `${cx + size * Math.cos(a)},${cy + size * Math.sin(a)}`;
  }).join(' ');
}

/* ─── ROUTE BUILDER ─── */
export default function RouteBuilder() {
  const [seed, setSeed] = useState(42);
  const [level, setLevel] = useState(() => generateLevel(42));
  const [path, setPath] = useState(['0,0']);
  const [sessionPoints, setSessionPoints] = useState(0);
  const [round, setRound] = useState(1);
  const [complete, setComplete] = useState(false);
  const [history, setHistory] = useState([]);

  const startId = '0,0';
  const endId = `${GRID_COLS - 1},${GRID_ROWS - 1}`;
  const currentPos = path[path.length - 1];

  const handleHexClick = useCallback((id) => {
    if (complete || level.blocked.has(id)) return;

    // If clicking the current cell, undo last step
    if (id === currentPos && path.length > 1) {
      setPath(p => p.slice(0, -1));
      return;
    }

    // Only allow adjacent cells
    const [cc, cr] = currentPos.split(',').map(Number);
    const [nc, nr] = id.split(',').map(Number);
    const isNeighbor = neighbors(cc, cr).some(([c, r]) => c === nc && r === nr);
    if (!isNeighbor) return;

    // No revisit
    if (path.includes(id)) return;

    const newPath = [...path, id];
    setPath(newPath);

    if (id === endId) {
      const steps = newPath.length - 1;
      const efficiency = Math.max(0, level.optimal + 3 - steps);
      const earned = 100 + efficiency * 20;
      setComplete(true);
      setSessionPoints(p => p + earned);
      setHistory(h => [...h, { round, steps, optimal: level.optimal, earned }].slice(-6));
    }
  }, [complete, level, currentPos, path, endId, round]);

  const newLevel = () => {
    const ns = seed + 1;
    setSeed(ns);
    setLevel(generateLevel(ns));
    setPath([startId]);
    setComplete(false);
    setRound(r => r + 1);
  };

  const reset = () => {
    setSeed(42);
    setLevel(generateLevel(42));
    setPath([startId]);
    setComplete(false);
    setRound(1);
    setSessionPoints(0);
    setHistory([]);
  };

  const SIZE = 28;
  const SVG_W = GRID_COLS * SIZE * 1.5 + SIZE * 1.5;
  const SVG_H = GRID_ROWS * SIZE * 1.75 + SIZE;

  return (
    <div style={{ minHeight: '100vh', padding: '40px 0 80px' }}>
      <div className="container" style={{ maxWidth: '960px' }}>

        <div style={{ marginBottom: '32px', fontSize: '0.82rem', color: 'rgba(168,207,224,0.4)' }}>
          <Link to="/" style={{ color: 'rgba(168,207,224,0.4)' }}>Home</Link>
          <span style={{ margin: '0 8px' }}>›</span>
          <Link to="/activities" style={{ color: 'rgba(168,207,224,0.4)' }}>Activities</Link>
          <span style={{ margin: '0 8px' }}>›</span>
          <span style={{ color: 'rgba(168,207,224,0.7)' }}>Route Builder</span>
        </div>

        <div style={{ marginBottom: '36px' }}>
          <div className="label label-amber" style={{ marginBottom: '12px' }}>Puzzle · Pathfinding</div>
          <h1 style={{ color: '#fff', fontSize: 'clamp(2rem, 5vw, 3.5rem)', marginBottom: '12px' }}>Route Builder</h1>
          <h2 style={{ color: 'rgba(168,207,224,0.85)', fontSize: '1.2rem', marginBottom: '12px' }}>Build the shortest safe path</h2>
          <p style={{ maxWidth: '500px' }}>
            Navigate from the north point to the south point across a hex-grid terrain. Fewer moves means more session points. Blocked cells are impassable.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '32px' }} className="rb-layout">

          {/* Grid */}
          <div>
            <div style={{
              background: 'rgba(13,31,53,0.8)',
              border: '1px solid rgba(168,207,224,0.1)',
              borderRadius: '16px', padding: '24px',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              gap: '16px',
            }}>
              <div style={{ fontSize: '0.8rem', color: 'rgba(168,207,224,0.5)', letterSpacing: '0.05em' }}>
                Move: {path.length - 1} · Optimal: {level.optimal} · Target: ≤{level.optimal + 3}
              </div>

              <svg viewBox={`0 0 ${SVG_W} ${SVG_H}`} style={{ width: '100%', maxWidth: '420px', overflow: 'visible' }}>
                {Array.from({ length: GRID_ROWS }, (_, r) =>
                  Array.from({ length: GRID_COLS }, (_, c) => {
                    const id = hexId(c, r);
                    const { x, y } = hexPos(c, r, SIZE);
                    const isBlocked = level.blocked.has(id);
                    const isPath = path.includes(id);
                    const isCurrent = id === currentPos;
                    const isStart = id === startId;
                    const isEnd = id === endId;
                    const isNeighbor = !complete && !isBlocked && !isPath &&
                      neighbors(...currentPos.split(',').map(Number)).some(([nc, nr]) => nc === c && nr === r);

                    let fill = 'rgba(26,53,80,0.6)';
                    if (isBlocked) fill = 'rgba(40,20,20,0.8)';
                    else if (isEnd) fill = complete ? 'rgba(100,200,100,0.25)' : 'rgba(201,168,76,0.15)';
                    else if (isStart) fill = 'rgba(74,127,165,0.3)';
                    else if (isCurrent) fill = 'rgba(74,127,165,0.5)';
                    else if (isPath) fill = 'rgba(74,127,165,0.25)';
                    else if (isNeighbor) fill = 'rgba(168,207,224,0.08)';

                    return (
                      <g key={id} onClick={() => handleHexClick(id)} style={{ cursor: isBlocked ? 'not-allowed' : isNeighbor || isCurrent ? 'pointer' : 'default' }}>
                        <polygon
                          points={hexPoints(x, y, SIZE - 2)}
                          fill={fill}
                          stroke={
                            isEnd ? '#c9a84c' :
                            isCurrent ? '#a8cfe0' :
                            isPath ? 'rgba(74,127,165,0.5)' :
                            isNeighbor ? 'rgba(168,207,224,0.25)' :
                            'rgba(168,207,224,0.06)'
                          }
                          strokeWidth={isEnd || isCurrent ? 1.5 : 1}
                        />
                        {isStart && <text x={x} y={y + 1} textAnchor="middle" dominantBaseline="middle" fontSize="10" fontFamily="Syne" fontWeight="700" fill="#a8cfe0">START</text>}
                        {isEnd && <text x={x} y={y + 1} textAnchor="middle" dominantBaseline="middle" fontSize="10" fontFamily="Syne" fontWeight="700" fill="#c9a84c">END</text>}
                        {isBlocked && <text x={x} y={y + 1} textAnchor="middle" dominantBaseline="middle" fontSize="14" fill="rgba(200,80,80,0.5)">✕</text>}
                        {isPath && !isStart && !isEnd && (
                          <circle cx={x} cy={y} r="4" fill="rgba(168,207,224,0.6)" />
                        )}
                      </g>
                    );
                  })
                )}

                {/* Path line */}
                {path.length > 1 && (
                  <polyline
                    points={path.map(id => {
                      const [c, r] = id.split(',').map(Number);
                      const { x, y } = hexPos(c, r, SIZE);
                      return `${x},${y}`;
                    }).join(' ')}
                    fill="none" stroke="rgba(74,127,165,0.5)" strokeWidth="2"
                    strokeDasharray="4 3" strokeLinecap="round"
                  />
                )}
              </svg>

              {/* Legend */}
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center' }}>
                {[
                  { color: 'rgba(74,127,165,0.5)', label: 'Your path' },
                  { color: 'rgba(40,20,20,0.8)', label: 'Blocked' },
                  { color: 'rgba(201,168,76,0.3)', label: 'Destination' },
                ].map(({ color, label }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.75rem', color: 'rgba(168,207,224,0.5)' }}>
                    <span style={{ width: '12px', height: '12px', background: color, borderRadius: '2px', flexShrink: 0 }} />
                    {label}
                  </div>
                ))}
              </div>

              {/* Click current to undo hint */}
              {!complete && path.length > 1 && (
                <div style={{ fontSize: '0.72rem', color: 'rgba(168,207,224,0.3)', textAlign: 'center' }}>
                  Click your current position to undo last step
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

            <div className="card">
              <div style={{ fontSize: '0.7rem', color: 'rgba(168,207,224,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>Session Points</div>
              <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '2.8rem', fontWeight: 800, color: '#c9a84c', lineHeight: 1 }}>{sessionPoints}</div>
              <div style={{ fontSize: '0.72rem', color: 'rgba(168,207,224,0.35)', marginTop: '6px' }}>No monetary value</div>
            </div>

            <AnimatePresence>
              {complete && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="card"
                  style={{ background: 'rgba(100,200,100,0.06)', border: '1px solid rgba(100,200,100,0.2)' }}
                >
                  <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1.1rem', color: '#80c880', marginBottom: '8px' }}>Route complete!</div>
                  <div style={{ fontSize: '0.85rem', color: 'rgba(168,207,224,0.6)', marginBottom: '14px' }}>
                    {path.length - 1} moves · Optimal: {level.optimal}
                  </div>
                  <button onClick={newLevel} className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '10px' }}>
                    Next terrain →
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {history.length > 0 && (
              <div className="card">
                <div style={{ fontSize: '0.7rem', color: 'rgba(168,207,224,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>Route History</div>
                {history.slice().reverse().map((h, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: i < history.length - 1 ? '1px solid rgba(168,207,224,0.06)' : 'none', fontSize: '0.8rem' }}>
                    <span style={{ color: 'rgba(168,207,224,0.5)' }}>Round {h.round} · {h.steps} moves</span>
                    <span style={{ color: '#c9a84c', fontWeight: 600 }}>+{h.earned}</span>
                  </div>
                ))}
              </div>
            )}

            <button onClick={reset} style={{ fontSize: '0.8rem', color: 'rgba(168,207,224,0.4)', background: 'none', border: '1px solid rgba(168,207,224,0.1)', borderRadius: '8px', padding: '8px', cursor: 'pointer' }}>
              Reset session
            </button>

            <div style={{ padding: '12px', background: 'rgba(168,207,224,0.03)', border: '1px solid rgba(168,207,224,0.07)', borderRadius: '10px', fontSize: '0.73rem', color: 'rgba(168,207,224,0.33)', lineHeight: 1.65 }}>
              Session points have no monetary value. Free entertainment activity. Adults 18+ only.
            </div>
          </div>
        </div>
      </div>
      <style>{`@media(min-width:768px){.rb-layout{grid-template-columns:1fr 260px !important;}}`}</style>
    </div>
  );
}
